import React, { createContext, useState, useContext, useEffect } from "react";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { CssBaseline } from "@mui/material";

export type ThemePresetName = "deepspace";

export const THEME_PRESET_NAMES: ThemePresetName[] = ["deepspace"];

export const THEME_PRESET_LABELS: Record<ThemePresetName, string> = {
  deepspace: "Deep Space",
};

interface PaletteColors {
  primary: { main: string; light?: string; dark?: string };
  secondary: { main: string; light?: string; dark?: string };
  background: { default: string; paper?: string };
  text?: { primary?: string; secondary?: string };
  divider?: string;
}

interface PresetPalettes {
  light: PaletteColors;
  dark: PaletteColors;
}

const PRESETS: Record<ThemePresetName, PresetPalettes> = {
  deepspace: {
    light: {
      primary: { main: "#000000", light: "#2a2a2a", dark: "#000000" },
      secondary: { main: "#607d8b", light: "#8eacbb", dark: "#34515e" },
      background: { default: "#f8f9fc", paper: "#ffffff" },
      text: { primary: "#000000", secondary: "#333333" },
      divider: "#cccccc",
    },
    dark: {
      primary: { main: "#ffffff", light: "#ffffff", dark: "#d4d4d4" },
      secondary: { main: "#90a4ae", light: "#c1d5e0", dark: "#62757f" },
      background: { default: "#080810", paper: "#101420" },
      text: { primary: "#ffffff", secondary: "#cccccc" },
      divider: "#444444",
    },
  },
};

interface ThemeContextProps {
  toggleTheme: () => void;
  mode: "light" | "dark";
  themePreset: ThemePresetName;
  setThemePreset: (preset: ThemePresetName) => void;
}

export const ThemeContext = createContext<ThemeContextProps | undefined>(
  undefined,
);

export const useThemeContext = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useThemeContext must be used within a ThemeProvider");
  }
  return context;
};

export const ThemeContextProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [mode, setMode] = useState<"light" | "dark">(
    () => (localStorage.getItem("theme") as "light" | "dark") || "light",
  );

  const [themePreset, setThemePreset] = useState<ThemePresetName>(
    () =>
      (localStorage.getItem("themePreset") as ThemePresetName) || "deepspace",
  );

  useEffect(() => {
    localStorage.setItem("theme", mode);
  }, [mode]);

  useEffect(() => {
    localStorage.setItem("themePreset", themePreset);
  }, [themePreset]);

  const colors = PRESETS[themePreset][mode];

  const theme = createTheme({
    palette: {
      mode: mode,
      primary: colors.primary,
      secondary: colors.secondary,
      background: colors.background,
      text: colors.text,
      divider: colors.divider,
    },
    typography: {
      fontFamily: '"Overlock", "Roboto", "Helvetica", "Arial", sans-serif',
      fontSize: 12,
      button: {
        textTransform: "none",
      },
    },
    shape: {
      borderRadius: 8,
    },
    components: {
      MuiAccordion: {
        styleOverrides: {
          root: {
            marginBottom: "16px",
            borderRadius: "8px !important",
            "&:last-of-type": {
              marginBottom: 0,
            },
            "&::before": {
              display: "none",
            },
          },
        },
      },
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            margin: 0,
            padding: 0,
            height: "100vh",
            display: "flex",
            flexDirection: "column",
            transition: "background-color 0.3s ease, color 0.3s ease",
          },
          "#root": {
            display: "flex",
            flexDirection: "column",
            minHeight: "100vh",
          },
          main: {
            flexGrow: 1,
            minHeight: "100vh",
            paddingTop: "50px",
          },
          a: {
            textDecoration: "none",
            color: colors.primary.main,
            transition: "color 0.2s ease",
            "&:hover": {
              textDecoration: "underline",
              color: colors.primary.dark || colors.primary.main,
            },
          },
        },
      },
      MuiToolbar: {
        styleOverrides: {
          root: {
            margin: 0,
            padding: 0,
          },
          gutters: {
            margin: 0,
            padding: 0,
          },
          regular: {
            margin: 0,
            padding: 0,
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            background: "transparent",
            boxShadow: "none",
            borderBottom: `1px solid ${colors.divider}`,
            color: colors.text?.primary || "inherit",
            backdropFilter: "blur(8px)",
            transition: "border-color 0.3s ease, color 0.3s ease",
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            fontSize: "0.8rem",
            padding: "6px 16px",
            borderRadius: "8px",
            textTransform: "none",
            transition: "all 0.2s ease",
            "&:hover": {
              boxShadow: "none",
            },
          },
          contained: {
            boxShadow: "none",
            "&:hover": {
              boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
            },
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            transition: "all 0.2s ease",
            "&.Mui-selected": {
              backgroundColor: "transparent",
              borderBottom: `3px solid ${colors.primary.main}`,
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
            transition: "border-color 0.3s ease, background-color 0.3s ease",
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
            transition: "border-color 0.3s ease, background-color 0.3s ease",
          },
        },
      },
    },
  });

  const toggleTheme = () => {
    setMode((prevMode) => (prevMode === "light" ? "dark" : "light"));
  };

  return (
    <ThemeContext.Provider
      value={{ toggleTheme, mode, themePreset, setThemePreset }}
    >
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeContext.Provider>
  );
};
